import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {RouterLink} from '@angular/router';
import {AdminApiService} from '../../admin-api.service';
import {
  AdminChatAnalyticsRecalculationStatus,
  AdminInfrastructureOverview,
  AdminInfrastructureHistory,
  AdminInfrastructurePod,
  AdminInfrastructurePodPoint,
  AdminInfrastructurePodSeries,
  AdminLevelRecalculationStatus,
  AdminSiteAnalyticsActionMetric,
  AdminSiteAnalyticsDailyPoint,
  AdminSiteAnalyticsOverview,
  AdminSiteAnalyticsPageMetric,
  AdminSiteAnalyticsTransitionMetric,
  AdminTwitchFollowerSyncStatus,
  AdminTwitchModerationAnalyticsOverview,
  AdminInfrastructureService,
  AdminInfrastructureServicePoint,
  AdminInfrastructureServiceSeries,
  AdminOverview
} from '../../admin.models';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {auditActionLabel} from '../../shared/audit-action-labels';
import {
  UiMetricLineChartComponent,
  UiMetricLineChartPoint,
  UiMetricLineChartSeries,
  UiButtonComponent,
  UiIconComponent,
  UiInputComponent,
  UiInfoTooltipComponent,
  UiTabComponent,
  UiTimeRangePickerComponent,
  UiTimeRangePickerLabels,
  UiTimeRangePickerValue,
  UiToastService
} from 'freyr-ui';

@Component({
  selector: 'app-overview-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, UiButtonComponent, UiIconComponent, UiInputComponent, UiInfoTooltipComponent, UiTabComponent, UiTimeRangePickerComponent, AdminTranslatePipe, UiMetricLineChartComponent],
  templateUrl: './overview-page.component.html',
  styleUrl: './overview-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewPageComponent implements OnInit, OnDestroy {
  readonly historyRangeOptions = [15, 60, 360, 1440];
  readonly historyQuickRangeOptions = [15, 30, 45, 60, 180, 360, 720, 1440, 4320, 10080];
  private readonly siteAnalyticsRefreshIntervalMs = 5_000;
  private readonly infrastructureRefreshIntervalMs = 15_000;
  private readonly chatAnalyticsRecalculationPollIntervalMs = 2_000;

  private readonly api = inject(AdminApiService);
  private readonly i18n = inject(AdminI18nService);
  private readonly toast = inject(UiToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private overviewEventSource: EventSource | null = null;
  private streamReconnectTimer: number | null = null;
  private monitoringPollTimer: number | null = null;
  private siteAnalyticsPollTimer: number | null = null;
  private infrastructurePollTimer: number | null = null;
  private chatAnalyticsRecalculationPollTimer: number | null = null;
  private levelRecalculationSocket: WebSocket | null = null;
  private levelRecalculationReconnectTimer: number | null = null;
  private twitchFollowerSyncSocket: WebSocket | null = null;
  private twitchFollowerSyncReconnectTimer: number | null = null;
  private streamConnected = false;
  private readonly chartPalette = ['#38bdf8', '#f59e0b', '#34d399', '#f87171', '#a78bfa', '#fb7185', '#22c55e', '#fde047'];
  private readonly serviceChartMetric = new Map<string, ServiceChartMetric>();
  private readonly defaultHistoryRangeMinutes = 180;
  private historyPickerValue: UiTimeRangePickerValue = {
    mode: 'relative',
    relativeMinutes: this.defaultHistoryRangeMinutes,
    absoluteFrom: '',
    absoluteTo: ''
  };
  private chartModalPickerValue: UiTimeRangePickerValue = {
    mode: 'relative',
    relativeMinutes: this.defaultHistoryRangeMinutes,
    absoluteFrom: '',
    absoluteTo: ''
  };
  private cachedTimeRangePickerLabelsLang: string | null = null;
  private cachedTimeRangePickerLabels: UiTimeRangePickerLabels | null = null;
  private readonly serviceChartSeriesCache = new Map<string, Record<ServiceChartMetric, UiMetricLineChartSeries[]>>();
  private readonly handleVisibilityChange = () => {
    if (!this.api.isMonitoringMode() && document.visibilityState === 'visible') {
      this.loadSiteAnalytics(true);
    }
  };
  private readonly handleWindowFocus = () => {
    if (!this.api.isMonitoringMode()) {
      this.loadSiteAnalytics(true);
    }
  };

  loading = false;
  overview: AdminOverview | null = null;
  siteAnalytics: AdminSiteAnalyticsOverview | null = null;
  chatAnalyticsRecalculation: AdminChatAnalyticsRecalculationStatus | null = null;
  chatAnalyticsRecalculationLoading = false;
  levelRecalculation: AdminLevelRecalculationStatus | null = null;
  levelRecalculationLoading = false;
  levelRecalculationUserId = '';
  twitchFollowerSync: AdminTwitchFollowerSyncStatus | null = null;
  twitchFollowerSyncLoading = false;
  twitchModerationAnalytics: AdminTwitchModerationAnalyticsOverview | null = null;
  history: AdminInfrastructureHistory | null = null;
  selectedHistoryRangeMinutes = this.defaultHistoryRangeMinutes;
  selectedHistoryAggregationIntervalMs = 60_000;
  selectedCommandExecutionAggregationIntervalMs = 60_000;
  commandExecutionRangePickerValue: UiTimeRangePickerValue = {
    mode: 'relative',
    relativeMinutes: this.defaultHistoryRangeMinutes,
    absoluteFrom: '',
    absoluteTo: ''
  };
  selectedModerationAggregationIntervalMs = 24 * 60 * 60 * 1000;
  moderationAnalyticsRangePickerValue: UiTimeRangePickerValue = {
    mode: 'relative',
    relativeMinutes: this.defaultHistoryRangeMinutes,
    absoluteFrom: '',
    absoluteTo: ''
  };
  historyMode: 'relative' | 'absolute' = 'relative';
  absoluteFromInput = '';
  absoluteToInput = '';
  chartModal: ChartModalState | null = null;
  sortedInfrastructureServices: AdminInfrastructureService[] = [];
  visibleWarnings: string[] = [];
  serviceDbChartSeries: UiMetricLineChartSeries[] = [];
  serviceDbPressureChartSeries: UiMetricLineChartSeries[] = [];
  serviceDbTimingChartSeries: UiMetricLineChartSeries[] = [];
  databaseLatencyChartSeries: UiMetricLineChartSeries[] = [];
  serviceHttpIncomingChartSeries: UiMetricLineChartSeries[] = [];
  serviceHttpOutgoingChartSeries: UiMetricLineChartSeries[] = [];
  serviceHttpErrorChartSeries: UiMetricLineChartSeries[] = [];
  serviceHttpLatencyChartSeries: UiMetricLineChartSeries[] = [];
  commandExecutionChartSeries: UiMetricLineChartSeries[] = [];
  chartModalSeries: UiMetricLineChartSeries[] = [];

  ngOnInit(): void {
    this.loadOverview();
    this.loadSiteAnalytics();
    this.loadChatAnalyticsRecalculationStatus(true);
    this.loadLevelRecalculationStatus(true);
    this.loadTwitchFollowerSyncStatus(true);
    this.loadHistory();
    if (this.api.isMonitoringMode()) {
      this.startMonitoringPolling();
    } else {
      this.connectOverviewStream();
      this.connectLevelRecalculationSocket();
      this.connectTwitchFollowerSyncSocket();
      this.startInfrastructurePolling();
      this.startSiteAnalyticsPolling();
    }
  }

  ngOnDestroy(): void {
    this.closeOverviewStream();
    this.stopMonitoringPolling();
    this.stopInfrastructurePolling();
    this.stopSiteAnalyticsPolling();
    this.stopChatAnalyticsRecalculationPolling();
    this.closeLevelRecalculationSocket();
    this.closeTwitchFollowerSyncSocket();
  }

  loadOverview(includeInfrastructure = false, silent = false): void {
    if (!silent) {
      this.loading = true;
    }
    const request$ = this.api.isMonitoringMode()
      ? this.api.getMonitoringOverview()
      : this.api.getOverview(includeInfrastructure);
    request$.subscribe({
      next: (overview) => {
        const nextOverview = this.withPreservedInfrastructure(overview);
        this.overview = nextOverview;
        this.recomputeOverviewDerivedState();
        if (this.api.isMonitoringMode() || overview.infrastructure) {
          this.mergeOverviewIntoHistory(nextOverview);
        }
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error: HttpErrorResponse) => {
        if (silent) {
          this.cdr.markForCheck();
          return;
        }
        this.loading = false;
        const message = typeof error.error?.message === 'string' && error.error.message.trim()
          ? error.error.message.trim()
          : this.i18n.t('admin.overview.loadError');
        this.toast.error(message);
        this.cdr.markForCheck();
      }
    });
  }

  loadSiteAnalytics(silent = false): void {
    if (this.api.isMonitoringMode()) {
      this.siteAnalytics = null;
      this.cdr.markForCheck();
      return;
    }
    this.api.getSiteAnalytics(14).subscribe({
      next: (analytics) => {
        this.siteAnalytics = analytics;
        this.cdr.markForCheck();
      },
      error: () => {
        if (silent) {
          return;
        }
        this.siteAnalytics = null;
        this.cdr.markForCheck();
      }
    });
    const moderationRange = this.resolveChartRange(this.moderationAnalyticsRangePickerValue);
    this.api.getTwitchModerationAnalytics(
      14,
      Math.floor(this.selectedModerationAggregationIntervalMs / 1000),
      moderationRange.from,
      moderationRange.to
    ).subscribe({
      next: (analytics) => {
        this.twitchModerationAnalytics = analytics;
        this.cdr.markForCheck();
      },
      error: () => {
        this.twitchModerationAnalytics = null;
        this.cdr.markForCheck();
      }
    });
  }

  loadChatAnalyticsRecalculationStatus(silent = false): void {
    if (this.api.isMonitoringMode()) {
      this.chatAnalyticsRecalculation = null;
      this.cdr.markForCheck();
      return;
    }

    this.api.getChatAnalyticsRecalculationStatus().subscribe({
      next: (status) => {
        this.chatAnalyticsRecalculation = status;
        if (this.isChatAnalyticsRecalculationRunning) {
          this.startChatAnalyticsRecalculationPolling();
        } else {
          this.stopChatAnalyticsRecalculationPolling();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        if (!silent) {
          this.toast.error(this.i18n.t('admin.overview.chatAnalyticsRecalculationLoadError'));
        }
        this.cdr.markForCheck();
      }
    });
  }

  loadLevelRecalculationStatus(silent = false): void {
    if (this.api.isMonitoringMode()) {
      this.levelRecalculation = null;
      this.cdr.markForCheck();
      return;
    }

    this.api.getLevelRecalculationStatus().subscribe({
      next: (status) => {
        this.levelRecalculation = status;
        this.cdr.markForCheck();
      },
      error: () => {
        if (!silent) {
          this.toast.error(this.i18n.t('admin.overview.levelRecalculationLoadError'));
        }
        this.cdr.markForCheck();
      }
    });
  }

  startChatAnalyticsRecalculation(): void {
    if (this.chatAnalyticsRecalculationLoading || this.isChatAnalyticsRecalculationRunning) {
      return;
    }

    this.chatAnalyticsRecalculationLoading = true;
    this.api.startChatAnalyticsRecalculation().subscribe({
      next: (status) => {
        this.chatAnalyticsRecalculation = status;
        this.chatAnalyticsRecalculationLoading = false;
        this.toast.success(this.i18n.t('admin.overview.chatAnalyticsRecalculationStarted'));
        this.startChatAnalyticsRecalculationPolling();
        this.cdr.markForCheck();
      },
      error: () => {
        this.chatAnalyticsRecalculationLoading = false;
        this.toast.error(this.i18n.t('admin.overview.chatAnalyticsRecalculationStartError'));
        this.cdr.markForCheck();
      }
    });
  }

  startLevelRecalculation(): void {
    if (this.levelRecalculationLoading || this.isLevelRecalculationRunning) {
      return;
    }
    this.levelRecalculationLoading = true;
    this.api.startLevelRecalculation(this.levelRecalculationUserId).subscribe({
      next: (result) => {
        this.levelRecalculation = result;
        this.levelRecalculationLoading = false;
        this.toast.success(this.i18n.t('admin.overview.levelRecalculationStarted'));
        this.cdr.markForCheck();
      },
      error: () => {
        this.levelRecalculationLoading = false;
        this.toast.error(this.i18n.t('admin.overview.levelRecalculationStartError'));
        this.cdr.markForCheck();
      }
    });
  }

  loadTwitchFollowerSyncStatus(silent = false): void {
    if (this.api.isMonitoringMode()) {
      this.twitchFollowerSync = null;
      this.cdr.markForCheck();
      return;
    }

    this.api.getTwitchFollowerSyncStatus().subscribe({
      next: (status) => {
        this.twitchFollowerSync = status;
        this.cdr.markForCheck();
      },
      error: () => {
        if (!silent) {
          this.toast.error(this.i18n.t('admin.overview.followersSyncLoadError'));
        }
        this.cdr.markForCheck();
      }
    });
  }

  startTwitchFollowerSync(): void {
    if (this.twitchFollowerSyncLoading || this.isTwitchFollowerSyncRunning) {
      return;
    }
    this.twitchFollowerSyncLoading = true;
    this.api.startTwitchFollowerSync().subscribe({
      next: (status) => {
        this.twitchFollowerSync = status;
        this.twitchFollowerSyncLoading = false;
        this.toast.success(this.i18n.t('admin.overview.followersSyncStarted'));
        this.cdr.markForCheck();
      },
      error: () => {
        this.twitchFollowerSyncLoading = false;
        this.toast.error(this.i18n.t('admin.overview.followersSyncStartError'));
        this.cdr.markForCheck();
      }
    });
  }

  get isChatAnalyticsRecalculationRunning(): boolean {
    return this.chatAnalyticsRecalculation?.status === 'RUNNING';
  }

  get isLevelRecalculationRunning(): boolean {
    return this.levelRecalculation?.status === 'RUNNING';
  }

  get isTwitchFollowerSyncRunning(): boolean {
    return this.twitchFollowerSync?.status === 'RUNNING';
  }

  get chatAnalyticsRecalculationProgress(): number {
    const status = this.chatAnalyticsRecalculation;
    if (!status || status.totalSessions <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((status.processedSessions / status.totalSessions) * 100));
  }

  chatAnalyticsRecalculationStatusLabel(status?: string | null): string {
    return this.i18n.t(`admin.overview.chatAnalyticsRecalculationStatus.${status || 'IDLE'}`);
  }

  get levelRecalculationProgress(): number {
    const status = this.levelRecalculation;
    if (!status || status.totalUsers <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((status.processedUsers / status.totalUsers) * 100));
  }

  levelRecalculationStatusLabel(status?: string | null): string {
    return this.i18n.t(`admin.overview.chatAnalyticsRecalculationStatus.${status || 'IDLE'}`);
  }

  get twitchFollowerSyncProgress(): number {
    const status = this.twitchFollowerSync;
    if (!status || status.totalUsers <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((status.processedUsers / status.totalUsers) * 100));
  }

  twitchFollowerSyncStatusLabel(status?: string | null): string {
    return this.i18n.t(`admin.overview.chatAnalyticsRecalculationStatus.${status || 'IDLE'}`);
  }

  loadHistory(): void {
    this.requestHistory(
      this.historyMode,
      this.selectedHistoryRangeMinutes,
      this.absoluteFromInput,
      this.absoluteToInput,
      this.selectedHistoryAggregationIntervalMs
    ).subscribe({
      next: (history) => {
        this.history = {
          ...history,
          from: this.resolveHistoryFrom(history.from, history.to),
          to: this.resolveHistoryTo(history.to)
        };
        this.recomputeHistoryDerivedState();
        this.cdr.markForCheck();
      },
      error: () => {
        this.history = null;
        this.recomputeHistoryDerivedState();
        this.cdr.markForCheck();
      }
    });
  }

  onHistoryAggregationIntervalChange(intervalMs: number): void {
    if (this.selectedHistoryAggregationIntervalMs === intervalMs) {
      return;
    }
    this.selectedHistoryAggregationIntervalMs = intervalMs;
    this.loadHistory();
  }

  openServiceChartModal(service: AdminInfrastructureService, metric: ServiceChartMetric): void {
    this.chartModal = this.createChartModalState(
      metric === 'cpu' ? 'service-cpu' : metric === 'memory' ? 'service-memory' : metric === 'pods' ? 'service-pods' : 'service-http',
      service.component,
      metric === 'cpu'
        ? this.i18n.t('admin.overview.cpuConsumptionChart')
        : metric === 'memory'
          ? this.i18n.t('admin.overview.memoryConsumptionChart')
          : metric === 'pods'
            ? this.i18n.t('admin.overview.podsEvolutionChart')
            : this.i18n.t('admin.overview.serviceHttpChart'),
      metric === 'cpu'
        ? this.i18n.t('admin.overview.servicePodCpuChartHint')
        : metric === 'memory'
          ? this.i18n.t('admin.overview.servicePodMemoryChartHint')
          : metric === 'pods'
            ? this.i18n.t('admin.overview.servicePodsChartHint')
            : this.i18n.t('admin.overview.serviceHttpChartHint'),
      metric === 'cpu' ? 'm' : metric === 'memory' ? ' MB' : metric === 'http' ? ' req/min' : ''
    );
    this.loadChartModalHistory();
  }

  openGlobalChartModal(kind: GlobalChartKind): void {
    switch (kind) {
      case 'db-connections':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.dbConnectionsChart'),
          this.i18n.t('admin.overview.dbConnectionsChartHint'),
          ''
        );
        break;
      case 'db-pressure':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.dbPressureChart'),
          this.i18n.t('admin.overview.dbPressureChartHint'),
          ''
        );
        break;
      case 'db-timing':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.dbTimingChart'),
          this.i18n.t('admin.overview.dbTimingChartHint'),
          ' ms'
        );
        break;
      case 'db-latency':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.dbLatencyChart'),
          this.i18n.t('admin.overview.dbLatencyChartHint'),
          ' ms'
        );
        break;
      case 'http-inbound':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.httpIncomingChart'),
          this.i18n.t('admin.overview.httpIncomingChartHint'),
          ' req/min'
        );
        break;
      case 'http-outbound':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.httpOutgoingChart'),
          this.i18n.t('admin.overview.httpOutgoingChartHint'),
          ' req/min'
        );
        break;
      case 'http-errors':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.httpErrorChart'),
          this.i18n.t('admin.overview.httpErrorChartHint'),
          ' err/min'
        );
        break;
      case 'http-latency':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.httpLatencyChart'),
          this.i18n.t('admin.overview.httpLatencyChartHint'),
          ' ms'
        );
        break;
      case 'command-executions':
        this.chartModal = this.createChartModalState(
          kind,
          null,
          this.i18n.t('admin.overview.commandExecutionsChart'),
          this.i18n.t('admin.overview.commandExecutionsChartHint'),
          ' cmd/min'
        );
        break;
    }
    this.loadChartModalHistory();
  }

  closeChartModal(): void {
    this.chartModal = null;
    this.chartModalSeries = [];
  }

  onChartModalAggregationIntervalChange(intervalMs: number): void {
    if (!this.chartModal || this.chartModal.selectedAggregationIntervalMs === intervalMs) {
      return;
    }
    this.chartModal.selectedAggregationIntervalMs = intervalMs;
    this.loadChartModalHistory();
  }

  private connectOverviewStream(): void {
    this.closeOverviewStream(false);
    try {
      this.overviewEventSource = new EventSource(this.api.overviewStreamUrl, { withCredentials: true });
      this.overviewEventSource.onopen = () => {
        this.streamConnected = true;
        this.cdr.markForCheck();
      };
      this.overviewEventSource.addEventListener('admin-overview', (event: MessageEvent<string>) => {
        this.streamConnected = true;
        this.loading = false;
        const overview = JSON.parse(event.data) as AdminOverview;
        this.overview = overview;
        this.recomputeOverviewDerivedState();
        this.mergeOverviewIntoHistory(overview);
        this.cdr.markForCheck();
      });
      this.overviewEventSource.onerror = () => {
        this.streamConnected = false;
        this.closeOverviewStream(false);
        this.scheduleStreamReconnect();
        this.cdr.markForCheck();
      };
    } catch {
      this.scheduleStreamReconnect();
    }
  }

  private scheduleStreamReconnect(): void {
    if (this.streamReconnectTimer != null) {
      return;
    }
    this.streamReconnectTimer = window.setTimeout(() => {
      this.streamReconnectTimer = null;
      if (!this.streamConnected) {
        this.loadOverview(true, true);
      }
      this.connectOverviewStream();
    }, 5000);
  }

  private closeOverviewStream(resetConnection = true): void {
    if (this.overviewEventSource) {
      this.overviewEventSource.close();
      this.overviewEventSource = null;
    }
    if (this.streamReconnectTimer != null) {
      window.clearTimeout(this.streamReconnectTimer);
      this.streamReconnectTimer = null;
    }
    if (resetConnection) {
      this.streamConnected = false;
    }
  }

  private connectLevelRecalculationSocket(): void {
    this.closeLevelRecalculationSocket(false);
    try {
      this.levelRecalculationSocket = new WebSocket(this.api.levelRecalculationWebSocketUrl);
      this.levelRecalculationSocket.onmessage = (event) => {
        try {
          this.levelRecalculation = JSON.parse(event.data) as AdminLevelRecalculationStatus;
          this.levelRecalculationLoading = false;
          this.cdr.markForCheck();
        } catch {
          // ignore malformed status frames
        }
      };
      this.levelRecalculationSocket.onclose = () => {
        this.closeLevelRecalculationSocket(false);
        this.scheduleLevelRecalculationSocketReconnect();
      };
      this.levelRecalculationSocket.onerror = () => {
        this.closeLevelRecalculationSocket(false);
        this.scheduleLevelRecalculationSocketReconnect();
      };
    } catch {
      this.scheduleLevelRecalculationSocketReconnect();
    }
  }

  private scheduleLevelRecalculationSocketReconnect(): void {
    if (this.levelRecalculationReconnectTimer != null || this.api.isMonitoringMode()) {
      return;
    }
    this.levelRecalculationReconnectTimer = window.setTimeout(() => {
      this.levelRecalculationReconnectTimer = null;
      this.loadLevelRecalculationStatus(true);
      this.connectLevelRecalculationSocket();
    }, 5000);
  }

  private closeLevelRecalculationSocket(clearReconnect = true): void {
    if (this.levelRecalculationSocket) {
      this.levelRecalculationSocket.onclose = null;
      this.levelRecalculationSocket.onerror = null;
      this.levelRecalculationSocket.onmessage = null;
      this.levelRecalculationSocket.close();
      this.levelRecalculationSocket = null;
    }
    if (clearReconnect && this.levelRecalculationReconnectTimer != null) {
      window.clearTimeout(this.levelRecalculationReconnectTimer);
      this.levelRecalculationReconnectTimer = null;
    }
  }

  private connectTwitchFollowerSyncSocket(): void {
    this.closeTwitchFollowerSyncSocket(false);
    try {
      this.twitchFollowerSyncSocket = new WebSocket(this.api.twitchFollowerSyncWebSocketUrl);
      this.twitchFollowerSyncSocket.onmessage = (event) => {
        try {
          this.twitchFollowerSync = JSON.parse(event.data) as AdminTwitchFollowerSyncStatus;
          this.twitchFollowerSyncLoading = false;
          this.cdr.markForCheck();
        } catch {
          // ignore malformed status frames
        }
      };
      this.twitchFollowerSyncSocket.onclose = () => {
        this.closeTwitchFollowerSyncSocket(false);
        this.scheduleTwitchFollowerSyncSocketReconnect();
      };
      this.twitchFollowerSyncSocket.onerror = () => {
        this.closeTwitchFollowerSyncSocket(false);
        this.scheduleTwitchFollowerSyncSocketReconnect();
      };
    } catch {
      this.scheduleTwitchFollowerSyncSocketReconnect();
    }
  }

  private scheduleTwitchFollowerSyncSocketReconnect(): void {
    if (this.twitchFollowerSyncReconnectTimer != null || this.api.isMonitoringMode()) {
      return;
    }
    this.twitchFollowerSyncReconnectTimer = window.setTimeout(() => {
      this.twitchFollowerSyncReconnectTimer = null;
      this.loadTwitchFollowerSyncStatus(true);
      this.connectTwitchFollowerSyncSocket();
    }, 5000);
  }

  private closeTwitchFollowerSyncSocket(clearReconnect = true): void {
    if (this.twitchFollowerSyncSocket) {
      this.twitchFollowerSyncSocket.onclose = null;
      this.twitchFollowerSyncSocket.onerror = null;
      this.twitchFollowerSyncSocket.onmessage = null;
      this.twitchFollowerSyncSocket.close();
      this.twitchFollowerSyncSocket = null;
    }
    if (clearReconnect && this.twitchFollowerSyncReconnectTimer != null) {
      window.clearTimeout(this.twitchFollowerSyncReconnectTimer);
      this.twitchFollowerSyncReconnectTimer = null;
    }
  }

  private startMonitoringPolling(): void {
    this.stopMonitoringPolling();
    this.monitoringPollTimer = window.setInterval(() => this.loadOverview(), 15000);
  }

  private stopMonitoringPolling(): void {
    if (this.monitoringPollTimer != null) {
      window.clearInterval(this.monitoringPollTimer);
      this.monitoringPollTimer = null;
    }
  }

  private startInfrastructurePolling(): void {
    this.stopInfrastructurePolling();
    this.infrastructurePollTimer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      this.loadOverview(true, true);
    }, this.infrastructureRefreshIntervalMs);
  }

  private stopInfrastructurePolling(): void {
    if (this.infrastructurePollTimer != null) {
      window.clearInterval(this.infrastructurePollTimer);
      this.infrastructurePollTimer = null;
    }
  }

  private startSiteAnalyticsPolling(): void {
    this.stopSiteAnalyticsPolling();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleWindowFocus);
    this.siteAnalyticsPollTimer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      this.loadSiteAnalytics(true);
    }, this.siteAnalyticsRefreshIntervalMs);
  }

  private stopSiteAnalyticsPolling(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleWindowFocus);
    if (this.siteAnalyticsPollTimer != null) {
      window.clearInterval(this.siteAnalyticsPollTimer);
      this.siteAnalyticsPollTimer = null;
    }
  }

  private startChatAnalyticsRecalculationPolling(): void {
    if (this.chatAnalyticsRecalculationPollTimer != null) {
      return;
    }
    this.chatAnalyticsRecalculationPollTimer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      this.loadChatAnalyticsRecalculationStatus(true);
    }, this.chatAnalyticsRecalculationPollIntervalMs);
  }

  private stopChatAnalyticsRecalculationPolling(): void {
    if (this.chatAnalyticsRecalculationPollTimer != null) {
      window.clearInterval(this.chatAnalyticsRecalculationPollTimer);
      this.chatAnalyticsRecalculationPollTimer = null;
    }
  }

  actionLabel(actionType: string): string {
    return auditActionLabel(actionType, this.i18n.currentLang);
  }

  formatPercent(value?: number | null): string {
    return value == null ? this.i18n.t('admin.common.notAvailable') : `${value.toFixed(1)}%`;
  }

  formatBytes(value?: number | null): string {
    if (value == null) {
      return this.i18n.t('admin.common.notAvailable');
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }

  formatCpuMilli(value?: number | null): string {
    return value == null ? this.i18n.t('admin.common.notAvailable') : `${value}m`;
  }

  formatDurationSeconds(value?: number | null): string {
    if (value == null) {
      return this.i18n.t('admin.common.notAvailable');
    }
    const totalSeconds = Math.max(0, Math.round(value));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes <= 0) {
      return `${seconds}s`;
    }
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  }

  analyticsLabel(value?: string | null): string {
    const normalized = (value || '').trim();
    if (!normalized) {
      return this.i18n.t('admin.common.notAvailable');
    }
    return normalized
      .replace(/^\/+/, '')
      .replace(/[:/_-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  analyticsTimelineWidth(point: AdminSiteAnalyticsDailyPoint): number {
    const maxViews = Math.max(...(this.siteAnalytics?.dailyTimeline || []).map((item) => item.pageViews), 1);
    return Math.max(12, Math.round((point.pageViews / maxViews) * 100));
  }

  get analyticsTopPages(): AdminSiteAnalyticsPageMetric[] {
    return this.siteAnalytics?.topPages || [];
  }

  get analyticsTopActions(): AdminSiteAnalyticsActionMetric[] {
    return this.siteAnalytics?.topActions || [];
  }

  get analyticsTopTransitions(): AdminSiteAnalyticsTransitionMetric[] {
    return this.siteAnalytics?.topTransitions || [];
  }

  get moderationTimelineChartSeries(): UiMetricLineChartSeries[] {
    return [
      {
        key: 'moderation-actions',
        label: 'Actions moderation',
        color: '#f59e0b',
        points: (this.twitchModerationAnalytics?.dailyTimeline ?? [])
          .map((point) => this.toChartPoint(point.bucketStart ?? point.date, point.totalActions, 24 * 60 * 60 * 1000, point.bucketEnd ?? undefined))
      }
    ];
  }

  infrastructureStatusClass(status?: string | null): string {
    switch ((status || '').toUpperCase()) {
      case 'UP':
        return 'status-pill status-pill--up';
      case 'DEGRADED':
        return 'status-pill status-pill--degraded';
      case 'DOWN':
      case 'UNAVAILABLE':
        return 'status-pill status-pill--down';
      default:
        return 'status-pill';
    }
  }

  trackService(_index: number, service: AdminInfrastructureService): string {
      return service.component;
    }

  trackPod(_index: number, pod: AdminInfrastructurePod): string {
    return pod.name;
  }

  selectedServiceChartMetric(component: string): ServiceChartMetric {
    return this.serviceChartMetric.get(component) || 'cpu';
  }

  setSelectedServiceChartMetric(component: string, metric: ServiceChartMetric): void {
    this.serviceChartMetric.set(component, metric);
    this.cdr.markForCheck();
  }

  serviceTypeBadgeLabel(component: string): string {
    const normalized = (component || '').trim().toLowerCase();
    if (['api', 'security'].includes(normalized)) {
      return 'API';
    }
    if (['web', 'admin'].includes(normalized)) {
      return 'WEB';
    }
    return 'SERVICE';
  }

  sortedServices(services: AdminInfrastructureService[] | null | undefined): AdminInfrastructureService[] {
    const typeOrder: Record<string, number> = {
      API: 0,
      WEB: 1,
      SERVICE: 2
    };

    return [...(services || [])].sort((left, right) => {
      const leftType = this.serviceTypeBadgeLabel(left.component);
      const rightType = this.serviceTypeBadgeLabel(right.component);
      const leftRank = typeOrder[leftType] ?? Number.MAX_SAFE_INTEGER;
      const rightRank = typeOrder[rightType] ?? Number.MAX_SAFE_INTEGER;

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return left.displayName.localeCompare(right.displayName, undefined, {sensitivity: 'base'});
    });
  }

  serviceVersionBadgeLabel(version?: string | null): string | null {
    if (!version || !version.trim()) {
      return null;
    }
    const normalized = version.trim();
    return normalized.startsWith('v') || normalized.startsWith('V') ? normalized : `v${normalized}`;
  }

  overviewMetricHelp(metric: OverviewMetricHelpKey): string {
    return this.i18n.t(`admin.overview.${metric}`);
  }

  podAvailabilityPercent(infra: AdminInfrastructureOverview): number | null {
    const services = infra.services || [];
    const totalPods = services.reduce((sum, service) => sum + service.totalPods, 0);
    if (totalPods <= 0) {
      return null;
    }
    const readyPods = services.reduce((sum, service) => sum + service.readyPods, 0);
    return (readyPods / totalPods) * 100;
  }

  visibleInfrastructureWarnings(infra: AdminInfrastructureOverview): string[] {
    return (infra.warnings || []).filter((warning) => {
      const normalized = warning.trim().toLowerCase();
      return normalized !== 'kubernetes probe disabled';
    });
  }

  get isMonitoringMode(): boolean {
    return this.api.isMonitoringMode();
  }

  get timeRangePickerLabels(): UiTimeRangePickerLabels {
    if (this.cachedTimeRangePickerLabelsLang !== this.i18n.currentLang || !this.cachedTimeRangePickerLabels) {
      this.cachedTimeRangePickerLabelsLang = this.i18n.currentLang;
      this.cachedTimeRangePickerLabels = {
        relative: this.i18n.t('admin.overview.relative'),
        absolute: this.i18n.t('admin.overview.absolute'),
        absoluteRange: this.i18n.t('admin.overview.absoluteRange'),
        custom: this.i18n.t('admin.overview.customRange'),
        quickPresets: this.i18n.t('admin.overview.quickPresets'),
        from: this.i18n.t('admin.overview.from'),
        to: this.i18n.t('admin.overview.to'),
        clear: this.i18n.t('admin.common.clear'),
        cancel: this.i18n.t('admin.common.cancel'),
        apply: this.i18n.t('admin.common.apply')
      };
    }
    return this.cachedTimeRangePickerLabels;
  }

  get timeRangePickerLocale(): string {
    return this.i18n.currentLang === 'fr' ? 'fr-FR' : 'en-US';
  }

  get historyRangePickerValue(): UiTimeRangePickerValue {
    if (this.historyPickerValue.mode !== this.historyMode
      || this.historyPickerValue.relativeMinutes !== this.selectedHistoryRangeMinutes
      || this.historyPickerValue.absoluteFrom !== this.absoluteFromInput
      || this.historyPickerValue.absoluteTo !== this.absoluteToInput) {
      this.historyPickerValue = {
        mode: this.historyMode,
        relativeMinutes: this.selectedHistoryRangeMinutes,
        absoluteFrom: this.absoluteFromInput,
        absoluteTo: this.absoluteToInput
      };
    }
    return this.historyPickerValue;
  }

  get chartModalRangePickerValue(): UiTimeRangePickerValue {
    const nextMode = this.chartModal?.historyMode ?? 'relative';
    const nextRelativeMinutes = this.chartModal?.selectedRangeMinutes ?? this.selectedHistoryRangeMinutes;
    const nextAbsoluteFrom = this.chartModal?.absoluteFromInput ?? '';
    const nextAbsoluteTo = this.chartModal?.absoluteToInput ?? '';
    if (this.chartModalPickerValue.mode !== nextMode
      || this.chartModalPickerValue.relativeMinutes !== nextRelativeMinutes
      || this.chartModalPickerValue.absoluteFrom !== nextAbsoluteFrom
      || this.chartModalPickerValue.absoluteTo !== nextAbsoluteTo) {
      this.chartModalPickerValue = {
        mode: nextMode,
        relativeMinutes: nextRelativeMinutes,
        absoluteFrom: nextAbsoluteFrom,
        absoluteTo: nextAbsoluteTo
      };
    }
    return this.chartModalPickerValue;
  }

  applyHistoryRangePickerValue(value: UiTimeRangePickerValue): void {
    const shouldReload = this.historyMode !== value.mode
      || this.selectedHistoryRangeMinutes !== value.relativeMinutes
      || this.absoluteFromInput !== value.absoluteFrom
      || this.absoluteToInput !== value.absoluteTo;
    this.historyMode = value.mode;
    this.selectedHistoryRangeMinutes = value.relativeMinutes;
    this.absoluteFromInput = value.absoluteFrom;
    this.absoluteToInput = value.absoluteTo;
    if (!shouldReload) {
      return;
    }
    this.loadHistory();
  }

  applyChartModalRangePickerValue(value: UiTimeRangePickerValue): void {
    if (!this.chartModal) {
      return;
    }
    const shouldReload = this.chartModal.historyMode !== value.mode
      || this.chartModal.selectedRangeMinutes !== value.relativeMinutes
      || this.chartModal.absoluteFromInput !== value.absoluteFrom
      || this.chartModal.absoluteToInput !== value.absoluteTo;
    this.chartModal.historyMode = value.mode;
    this.chartModal.selectedRangeMinutes = value.relativeMinutes;
    this.chartModal.absoluteFromInput = value.absoluteFrom;
    this.chartModal.absoluteToInput = value.absoluteTo;
    if (!shouldReload) {
      return;
    }
    this.loadChartModalHistory();
  }

  onCommandExecutionAggregationIntervalChange(intervalMs: number): void {
    if (this.selectedCommandExecutionAggregationIntervalMs === intervalMs) {
      return;
    }
    this.selectedCommandExecutionAggregationIntervalMs = intervalMs;
    this.loadCommandExecutionHistory();
  }

  onCommandExecutionRangeChange(value: UiTimeRangePickerValue): void {
    this.commandExecutionRangePickerValue = value;
    this.loadCommandExecutionHistory();
  }

  onModerationAnalyticsAggregationIntervalChange(intervalMs: number): void {
    if (this.selectedModerationAggregationIntervalMs === intervalMs) {
      return;
    }
    this.selectedModerationAggregationIntervalMs = intervalMs;
    this.loadSiteAnalytics(true);
  }

  onModerationAnalyticsRangeChange(value: UiTimeRangePickerValue): void {
    this.moderationAnalyticsRangePickerValue = value;
    this.loadSiteAnalytics(true);
  }

  private loadCommandExecutionHistory(): void {
    const value = this.commandExecutionRangePickerValue;
    this.requestHistory(
      value.mode,
      value.relativeMinutes,
      value.absoluteFrom,
      value.absoluteTo,
      this.selectedCommandExecutionAggregationIntervalMs
    ).subscribe({
      next: (history) => {
        this.commandExecutionChartSeries = this.commandExecutionChartSeriesFor(history);
        this.cdr.markForCheck();
      },
      error: () => {
        this.commandExecutionChartSeries = [];
        this.cdr.markForCheck();
      }
    });
  }

  servicePodChartTitle(component: string): string {
    return this.selectedServiceChartMetric(component) === 'cpu'
      ? this.i18n.t('admin.overview.cpuChart')
      : this.selectedServiceChartMetric(component) === 'memory'
        ? this.i18n.t('admin.overview.memoryChart')
        : this.selectedServiceChartMetric(component) === 'pods'
          ? this.i18n.t('admin.overview.podsChart')
          : this.i18n.t('admin.overview.httpChart');
  }

  servicePodChartSubtitle(component: string): string {
    return this.selectedServiceChartMetric(component) === 'cpu'
      ? this.i18n.t('admin.overview.servicePodCpuChartHint')
      : this.selectedServiceChartMetric(component) === 'memory'
        ? this.i18n.t('admin.overview.servicePodMemoryChartHint')
        : this.selectedServiceChartMetric(component) === 'pods'
          ? this.i18n.t('admin.overview.servicePodsChartHint')
          : this.i18n.t('admin.overview.serviceHttpChartHint');
  }

  servicePodChartValueSuffix(component: string): string {
    return this.selectedServiceChartMetric(component) === 'cpu'
      ? 'm'
      : this.selectedServiceChartMetric(component) === 'memory'
        ? ' MB'
        : this.selectedServiceChartMetric(component) === 'http'
          ? ' req/min'
          : '';
  }

  servicePodChartInfoTooltip(component: string): string {
    return this.selectedServiceChartMetric(component) === 'cpu'
      ? this.i18n.t('admin.overview.helpCpuChart')
      : this.selectedServiceChartMetric(component) === 'memory'
        ? this.i18n.t('admin.overview.helpMemoryChart')
        : this.selectedServiceChartMetric(component) === 'pods'
          ? this.i18n.t('admin.overview.helpPodsChart')
          : this.i18n.t('admin.overview.helpHttpChart');
  }

  servicePodChartSeries(component: string): UiMetricLineChartSeries[] {
    const cachedSeries = this.serviceChartSeriesCache.get(component);
    if (!cachedSeries) {
      return [];
    }
    return cachedSeries[this.selectedServiceChartMetric(component)];
  }

  get historyRangeStart(): string | null {
    if (this.historyMode === 'absolute' && this.absoluteFromInput) {
      return this.toIsoFromLocalInput(this.absoluteFromInput);
    }
    return this.history?.from || null;
  }

  get historyRangeEnd(): string | null {
    if (this.historyMode === 'absolute' && this.absoluteToInput) {
      return this.toIsoFromLocalInput(this.absoluteToInput);
    }
    return this.history?.to || null;
  }

  get absoluteRangeValid(): boolean {
    if (!this.absoluteFromInput || !this.absoluteToInput) {
      return false;
    }
    return Date.parse(this.toIsoFromLocalInput(this.absoluteFromInput)) <= Date.parse(this.toIsoFromLocalInput(this.absoluteToInput));
  }

  get chartModalHistoryRangeStart(): string | null {
    if (!this.chartModal) {
      return null;
    }
    if (this.chartModal.historyMode === 'absolute' && this.chartModal.absoluteFromInput) {
      return this.toIsoFromLocalInput(this.chartModal.absoluteFromInput);
    }
    return this.chartModal.history?.from || null;
  }

  get chartModalHistoryRangeEnd(): string | null {
    if (!this.chartModal) {
      return null;
    }
    if (this.chartModal.historyMode === 'absolute' && this.chartModal.absoluteToInput) {
      return this.toIsoFromLocalInput(this.chartModal.absoluteToInput);
    }
    return this.chartModal.history?.to || null;
  }

  private serviceDbChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return (history?.services || [])
      .flatMap((serie, index) => {
        const color = this.chartPalette[index % this.chartPalette.length];
        return [
          {
            key: `db-active-${serie.component}`,
            label: `${serie.displayName} active`,
            color,
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.activeDbConnections ?? null
            })), history)
          },
          {
            key: `db-idle-${serie.component}`,
            label: `${serie.displayName} idle`,
            color: this.withAlpha(color, .45),
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.idleDbConnections ?? null
            })), history)
          },
          {
            key: `db-max-${serie.component}`,
            label: `${serie.displayName} max`,
            color: this.withAlpha(color, .22),
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.maxDbConnections ?? null
            })), history)
          }
        ];
      })
      .filter((serie) => serie.points.some((point) => point.value != null));
  }

  private serviceDbPressureChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return (history?.services || [])
      .flatMap((serie, index) => {
        const color = this.chartPalette[index % this.chartPalette.length];
        return [
          {
            key: `db-pending-${serie.component}`,
            label: `${serie.displayName} pending`,
            color,
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.pendingDbConnections ?? null
            })), history)
          },
          {
            key: `db-timeout-${serie.component}`,
            label: `${serie.displayName} timeout`,
            color: this.withAlpha(color, .45),
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.connectionTimeoutCount ?? null
            })), history)
          }
        ];
      })
      .filter((serie) => serie.points.some((point) => point.value != null));
  }

  private serviceDbTimingChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return (history?.services || [])
      .flatMap((serie, index) => {
        const color = this.chartPalette[index % this.chartPalette.length];
        return [
          {
            key: `db-acquire-${serie.component}`,
            label: `${serie.displayName} acquire`,
            color,
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.connectionAcquireMs ?? null
            })), history)
          },
          {
            key: `db-creation-${serie.component}`,
            label: `${serie.displayName} creation`,
            color: this.withAlpha(color, .6),
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.connectionCreationMs ?? null
            })), history)
          },
          {
            key: `db-usage-${serie.component}`,
            label: `${serie.displayName} usage`,
            color: this.withAlpha(color, .3),
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.connectionUsageMs ?? null
            })), history)
          }
        ];
      })
      .filter((serie) => serie.points.some((point) => point.value != null));
  }

  private databaseLatencyChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return [{
      key: 'database-latency',
      label: 'DB latency',
      color: '#38bdf8',
      points: this.toBucketedHistoryPoints((history?.database || []).map((point) => ({
        timestamp: point.capturedAt,
        bucketStart: point.bucketStart,
        bucketEnd: point.bucketEnd,
        value: point.latencyMs ?? null
      })), history)
    }].filter((serie) => serie.points.some((point) => point.value != null));
  }

  private commandExecutionChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return [{
      key: 'command-executions',
      label: this.i18n.t('admin.overview.commandExecutionsLegend'),
      color: '#f59e0b',
      points: this.toBucketedHistoryPoints((history?.commandExecutions || []).map((point) => ({
        timestamp: point.capturedAt,
        bucketStart: point.bucketStart,
        bucketEnd: point.bucketEnd,
        value: point.executedCount
      })), history)
    }].filter((serie) => serie.points.some((point) => point.value != null));
  }

  private serviceHttpIncomingChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return this.buildHttpCounterRateSeries(history, 'incomingHttpRequestCount', this.i18n.t('admin.overview.incomingLabel'));
  }

  private serviceHttpOutgoingChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return this.buildHttpCounterRateSeries(history, 'outgoingHttpRequestCount', this.i18n.t('admin.overview.outgoingLabel'));
  }

  private serviceHttpErrorChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return [
      ...this.buildHttpCounterRateSeries(history, 'incomingHttpErrorCount', this.i18n.t('admin.overview.incomingErrorLabel')),
      ...this.buildHttpCounterRateSeries(history, 'outgoingHttpErrorCount', this.i18n.t('admin.overview.outgoingErrorLabel'), .42)
    ];
  }

  private serviceHttpLatencyChartSeriesFor(history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartSeries[] {
    return (history?.services || [])
      .flatMap((serie, index) => {
        const color = this.chartPalette[index % this.chartPalette.length];
        return [
          {
            key: `http-in-latency-${serie.component}`,
            label: `${serie.displayName} ${this.i18n.t('admin.overview.incomingShortLabel')}`,
            color,
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.incomingHttpLatencyMs ?? null
            })), history)
          },
          {
            key: `http-out-latency-${serie.component}`,
            label: `${serie.displayName} ${this.i18n.t('admin.overview.outgoingShortLabel')}`,
            color: this.withAlpha(color, .42),
            points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
              timestamp: point.capturedAt,
              bucketStart: point.bucketStart,
              bucketEnd: point.bucketEnd,
              value: point.outgoingHttpLatencyMs ?? null
            })), history)
          }
        ];
      })
      .filter((serie) => serie.points.some((point) => point.value != null));
  }

  private mergeOverviewIntoHistory(overview: AdminOverview): void {
    const infra = overview.infrastructure;
    if (!infra?.generatedAt) {
      return;
    }
    if (this.historyMode === 'absolute' && !this.isTimestampInsideCurrentRange(infra.generatedAt)) {
      return;
    }

    const nextHistory: AdminInfrastructureHistory = this.history
      ? structuredClone(this.history)
      : { database: [], services: [], pods: [] };

    nextHistory.to = this.historyMode === 'absolute' ? this.resolveHistoryTo(nextHistory.to) : infra.generatedAt;
    nextHistory.from = this.resolveHistoryFrom(nextHistory.from, nextHistory.to || infra.generatedAt);

    if (infra.database) {
      nextHistory.database = this.trimPoints([
        ...(nextHistory.database || []),
        {
          capturedAt: infra.generatedAt,
          status: infra.database.status,
          reachable: infra.database.reachable,
          latencyMs: infra.database.latencyMs ?? null
        }
      ]);
    }

    nextHistory.services = this.mergeServiceHistory(nextHistory.services || [], infra.generatedAt, infra.services || []);
    nextHistory.pods = this.mergePodHistory(nextHistory.pods || [], infra.generatedAt, infra.pods || []);
    this.history = nextHistory;
    this.recomputeHistoryDerivedState();
  }

  private mergeServiceHistory(history: AdminInfrastructureServiceSeries[],
                              capturedAt: string,
                              services: AdminInfrastructureService[]): AdminInfrastructureServiceSeries[] {
    const seriesByComponent = new Map(history.map((serie) => [serie.component, structuredClone(serie)]));
    services.forEach((service) => {
      const existing = seriesByComponent.get(service.component) || {
        component: service.component,
        displayName: service.displayName,
        points: []
      };
      existing.displayName = service.displayName;
      existing.points = this.trimPoints<ServicePointLike>([
        ...existing.points,
        {
          capturedAt,
          overallStatus: service.overallStatus,
          totalPods: service.totalPods,
          readyPods: service.readyPods,
          restartCount: service.restartCount,
          cpuUsagePercent: service.cpuUsagePercent ?? null,
          memoryUsageBytes: service.memoryUsageBytes ?? null,
          activeDbConnections: service.activeDbConnections ?? null,
          idleDbConnections: service.idleDbConnections ?? null,
          pendingDbConnections: service.pendingDbConnections ?? null,
          maxDbConnections: service.maxDbConnections ?? null,
          connectionAcquireMs: service.connectionAcquireMs ?? null,
          connectionCreationMs: service.connectionCreationMs ?? null,
          connectionUsageMs: service.connectionUsageMs ?? null,
          connectionTimeoutCount: service.connectionTimeoutCount ?? null,
          incomingHttpRequestCount: service.incomingHttpRequestCount ?? null,
          incomingHttpErrorCount: service.incomingHttpErrorCount ?? null,
          incomingHttpLatencyMs: service.incomingHttpLatencyMs ?? null,
          outgoingHttpRequestCount: service.outgoingHttpRequestCount ?? null,
          outgoingHttpErrorCount: service.outgoingHttpErrorCount ?? null,
          outgoingHttpLatencyMs: service.outgoingHttpLatencyMs ?? null
        }
      ]) as AdminInfrastructureServicePoint[];
      seriesByComponent.set(service.component, existing);
    });
    return [...seriesByComponent.values()];
  }

  private mergePodHistory(history: AdminInfrastructurePodSeries[],
                          capturedAt: string,
                          pods: AdminInfrastructurePod[]): AdminInfrastructurePodSeries[] {
    const seriesByPod = new Map(history.map((serie) => [serie.podName, structuredClone(serie)]));
    pods.forEach((pod) => {
      const existing = seriesByPod.get(pod.name) || {
        podName: pod.name,
        component: pod.component,
        points: []
      };
      existing.component = pod.component;
      existing.points = this.trimPoints<PodPointLike>([
        ...existing.points,
        {
          capturedAt,
          phase: pod.phase,
          healthStatus: pod.healthStatus,
          ready: pod.ready,
          restartCount: pod.restartCount,
          cpuMilliCores: pod.cpuMilliCores ?? null,
          memoryBytes: pod.memoryBytes ?? null
        }
      ]) as AdminInfrastructurePodPoint[];
      seriesByPod.set(pod.name, existing);
    });
    return [...seriesByPod.values()];
  }

  private trimPoints<T extends { capturedAt: string }>(points: T[]): T[] {
    const threshold = Date.parse(this.resolveHistoryFrom(this.history?.from, this.resolveHistoryTo(this.history?.to)));
    const end = Date.parse(this.resolveHistoryTo(this.history?.to));
    return points
      .sort((left, right) => Date.parse(left.capturedAt) - Date.parse(right.capturedAt))
      .filter((point) => {
        const capturedAt = Date.parse(point.capturedAt);
        return capturedAt >= threshold && capturedAt <= end;
      });
  }

  private resolveHistoryFrom(existingFrom?: string | null, referenceTo?: string | null): string {
    if (this.historyMode === 'absolute' && this.absoluteFromInput) {
      return this.toIsoFromLocalInput(this.absoluteFromInput);
    }
    const end = referenceTo ? Date.parse(referenceTo) : Date.now();
    const computedFrom = new Date(end - this.selectedHistoryRangeMinutes * 60 * 1000).toISOString();
    if (!existingFrom) {
      return computedFrom;
    }
    const parsedExisting = Date.parse(existingFrom);
    return Number.isFinite(parsedExisting) ? new Date(Math.max(parsedExisting, end - this.selectedHistoryRangeMinutes * 60 * 1000)).toISOString() : computedFrom;
  }

  private resolveHistoryTo(existingTo?: string | null): string {
    if (this.historyMode === 'absolute' && this.absoluteToInput) {
      return this.toIsoFromLocalInput(this.absoluteToInput);
    }
    return existingTo || new Date().toISOString();
  }

  private createChartModalState(kind: ChartModalKind,
                                component: string | null,
                                title: string,
                                subtitle: string,
                                valueSuffix: string): ChartModalState {
    return {
      kind,
      component,
      title,
      subtitle,
      infoTooltip: this.chartModalInfoTooltip(kind),
      valueSuffix,
      history: null,
      loading: false,
      historyMode: this.historyMode,
      selectedRangeMinutes: this.selectedHistoryRangeMinutes,
      selectedAggregationIntervalMs: this.selectedHistoryAggregationIntervalMs,
      absoluteFromInput: this.absoluteFromInput,
      absoluteToInput: this.absoluteToInput
    };
  }

  private loadChartModalHistory(): void {
    if (!this.chartModal) {
      return;
    }
    this.chartModal.loading = true;
    this.requestHistory(
      this.chartModal.historyMode,
      this.chartModal.selectedRangeMinutes,
      this.chartModal.absoluteFromInput,
      this.chartModal.absoluteToInput,
      this.chartModal.selectedAggregationIntervalMs
    ).subscribe({
      next: (history) => {
        if (!this.chartModal) {
          return;
        }
        this.chartModal.history = {
          ...history,
          from: this.resolveModalHistoryFrom(history.from, history.to),
          to: this.resolveModalHistoryTo(history.to)
        };
        this.chartModal.loading = false;
        this.recomputeChartModalSeries();
        this.cdr.markForCheck();
      },
      error: () => {
        if (!this.chartModal) {
          return;
        }
        this.chartModal.history = null;
        this.chartModal.loading = false;
        this.recomputeChartModalSeries();
        this.cdr.markForCheck();
      }
    });
  }

  private recomputeOverviewDerivedState(): void {
    const infra = this.overview?.infrastructure;
    this.sortedInfrastructureServices = this.sortedServices(infra?.services);
    this.visibleWarnings = infra ? this.visibleInfrastructureWarnings(infra) : [];
  }

  private recomputeHistoryDerivedState(): void {
    const history = this.history;
    this.serviceDbChartSeries = this.serviceDbChartSeriesFor(history);
    this.serviceDbPressureChartSeries = this.serviceDbPressureChartSeriesFor(history);
    this.serviceDbTimingChartSeries = this.serviceDbTimingChartSeriesFor(history);
    this.databaseLatencyChartSeries = this.databaseLatencyChartSeriesFor(history);
    this.serviceHttpIncomingChartSeries = this.serviceHttpIncomingChartSeriesFor(history);
    this.serviceHttpOutgoingChartSeries = this.serviceHttpOutgoingChartSeriesFor(history);
    this.serviceHttpErrorChartSeries = this.serviceHttpErrorChartSeriesFor(history);
    this.serviceHttpLatencyChartSeries = this.serviceHttpLatencyChartSeriesFor(history);
    this.commandExecutionChartSeries = this.commandExecutionChartSeriesFor(history);

    this.serviceChartSeriesCache.clear();
    const components = new Set<string>([
      ...(this.overview?.infrastructure?.services || []).map((service) => service.component),
      ...(history?.services || []).map((serie) => serie.component),
      ...(history?.pods || []).map((serie) => serie.component)
    ]);

    components.forEach((component) => {
      this.serviceChartSeriesCache.set(component, {
        cpu: this.podSeriesFor(component, 'cpu', history),
        memory: this.podSeriesFor(component, 'memory', history),
        pods: this.servicePodsChartSeriesFor(component, history),
        http: this.serviceHttpChartSeriesFor(component, history)
      });
    });

    this.recomputeChartModalSeries();
  }

  private recomputeChartModalSeries(): void {
    if (!this.chartModal?.history) {
      this.chartModalSeries = [];
      return;
    }

    switch (this.chartModal.kind) {
      case 'service-cpu':
        this.chartModalSeries = this.podSeriesFor(this.chartModal.component || '', 'cpu', this.chartModal.history);
        return;
      case 'service-memory':
        this.chartModalSeries = this.podSeriesFor(this.chartModal.component || '', 'memory', this.chartModal.history);
        return;
      case 'service-pods':
        this.chartModalSeries = this.servicePodsChartSeriesFor(this.chartModal.component || '', this.chartModal.history);
        return;
      case 'service-http':
        this.chartModalSeries = this.serviceHttpChartSeriesFor(this.chartModal.component || '', this.chartModal.history);
        return;
      case 'db-connections':
        this.chartModalSeries = this.serviceDbChartSeriesFor(this.chartModal.history);
        return;
      case 'db-pressure':
        this.chartModalSeries = this.serviceDbPressureChartSeriesFor(this.chartModal.history);
        return;
      case 'db-timing':
        this.chartModalSeries = this.serviceDbTimingChartSeriesFor(this.chartModal.history);
        return;
      case 'db-latency':
        this.chartModalSeries = this.databaseLatencyChartSeriesFor(this.chartModal.history);
        return;
      case 'http-inbound':
        this.chartModalSeries = this.serviceHttpIncomingChartSeriesFor(this.chartModal.history);
        return;
      case 'http-outbound':
        this.chartModalSeries = this.serviceHttpOutgoingChartSeriesFor(this.chartModal.history);
        return;
      case 'http-errors':
        this.chartModalSeries = this.serviceHttpErrorChartSeriesFor(this.chartModal.history);
        return;
      case 'http-latency':
        this.chartModalSeries = this.serviceHttpLatencyChartSeriesFor(this.chartModal.history);
        return;
      case 'command-executions':
        this.chartModalSeries = this.commandExecutionChartSeriesFor(this.chartModal.history);
        return;
    }
  }

  private withPreservedInfrastructure(overview: AdminOverview): AdminOverview {
    if (overview.infrastructure || !this.overview?.infrastructure) {
      return overview;
    }
    return {
      ...overview,
      infrastructure: this.overview.infrastructure
    };
  }

  private buildHistoryRequest(mode: 'relative' | 'absolute',
                              minutes: number,
                              absoluteFromInput: string,
                              absoluteToInput: string,
                              aggregationIntervalMs: number): { minutes?: number; from?: string | null; to?: string | null; granularitySeconds?: number | null } {
    const granularitySeconds = Math.max(1, Math.floor(aggregationIntervalMs / 1000));
    return mode === 'absolute'
      ? {
        from: absoluteFromInput ? this.toIsoFromLocalInput(absoluteFromInput) : null,
        to: absoluteToInput ? this.toIsoFromLocalInput(absoluteToInput) : null,
        granularitySeconds
      }
      : { minutes, granularitySeconds };
  }

  private resolveChartRange(value: UiTimeRangePickerValue): { from: string | null; to: string | null } {
    if (value.mode === 'absolute') {
      return {
        from: value.absoluteFrom ? this.toIsoFromLocalInput(value.absoluteFrom) : null,
        to: value.absoluteTo ? this.toIsoFromLocalInput(value.absoluteTo) : null
      };
    }
    const to = new Date();
    const from = new Date(to.getTime() - Math.max(value.relativeMinutes, 1) * 60_000);
    return {
      from: from.toISOString(),
      to: to.toISOString()
    };
  }

  private requestHistory(mode: 'relative' | 'absolute',
                         minutes: number,
                         absoluteFromInput: string,
                         absoluteToInput: string,
                         aggregationIntervalMs: number) {
    const request = this.buildHistoryRequest(mode, minutes, absoluteFromInput, absoluteToInput, aggregationIntervalMs);
    return this.api.isMonitoringMode()
      ? this.api.getMonitoringOverviewHistory(request)
      : this.api.getOverviewHistory(request);
  }

  private resolveModalHistoryFrom(existingFrom?: string | null, referenceTo?: string | null): string {
    if (this.chartModal?.historyMode === 'absolute' && this.chartModal.absoluteFromInput) {
      return this.toIsoFromLocalInput(this.chartModal.absoluteFromInput);
    }
    const end = referenceTo ? Date.parse(referenceTo) : Date.now();
    const minutes = this.chartModal?.selectedRangeMinutes ?? this.defaultHistoryRangeMinutes;
    const computedFrom = new Date(end - minutes * 60 * 1000).toISOString();
    if (!existingFrom) {
      return computedFrom;
    }
    return existingFrom;
  }

  private resolveModalHistoryTo(existingTo?: string | null): string {
    if (this.chartModal?.historyMode === 'absolute' && this.chartModal.absoluteToInput) {
      return this.toIsoFromLocalInput(this.chartModal.absoluteToInput);
    }
    return existingTo || new Date().toISOString();
  }

  private toIsoFromLocalInput(value: string): string {
    return new Date(value).toISOString();
  }

  private chartModalInfoTooltip(kind: ChartModalKind): string {
    switch (kind) {
      case 'service-cpu':
        return this.i18n.t('admin.overview.helpCpuChart');
      case 'service-memory':
        return this.i18n.t('admin.overview.helpMemoryChart');
      case 'service-pods':
        return this.i18n.t('admin.overview.helpPodsChart');
      case 'service-http':
        return this.i18n.t('admin.overview.helpHttpChart');
      case 'db-connections':
        return this.i18n.t('admin.overview.helpDbConnectionsChart');
      case 'db-pressure':
        return this.i18n.t('admin.overview.helpDbPressureChart');
      case 'db-timing':
        return this.i18n.t('admin.overview.helpDbTimingChart');
      case 'db-latency':
        return this.i18n.t('admin.overview.helpDbLatencyChart');
      case 'http-inbound':
        return this.i18n.t('admin.overview.helpHttpIncomingChart');
      case 'http-outbound':
        return this.i18n.t('admin.overview.helpHttpOutgoingChart');
      case 'http-errors':
        return this.i18n.t('admin.overview.helpHttpErrorChart');
      case 'http-latency':
        return this.i18n.t('admin.overview.helpHttpLatencyChart');
      case 'command-executions':
        return this.i18n.t('admin.overview.helpCommandExecutionsChart');
    }
  }

  private isTimestampInsideCurrentRange(timestamp: string): boolean {
    const time = Date.parse(timestamp);
    const start = Date.parse(this.historyRangeStart || '');
    const end = Date.parse(this.historyRangeEnd || '');
    return (!Number.isFinite(start) || time >= start) && (!Number.isFinite(end) || time <= end);
  }

  private withAlpha(hexColor: string, alpha: number): string {
    const normalized = hexColor.replace('#', '');
    if (normalized.length !== 6) {
      return hexColor;
    }
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private podSeriesFor(component: string, metric: 'cpu' | 'memory', history: AdminInfrastructureHistory | null | undefined = this.history): UiMetricLineChartSeries[] {
    return (history?.pods || [])
      .filter((serie) => serie.component === component)
      .map((serie, index) => ({
        key: `${metric}-${serie.podName}`,
        label: serie.podName,
        color: this.chartPalette[index % this.chartPalette.length],
        points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
          timestamp: point.capturedAt,
          bucketStart: point.bucketStart,
          bucketEnd: point.bucketEnd,
          value: metric === 'cpu'
            ? (point.cpuMilliCores ?? null)
            : (point.memoryBytes != null ? point.memoryBytes / (1024 * 1024) : null)
        })), history)
      }))
      .filter((serie) => serie.points.some((point) => point.value != null));
  }

  private servicePodsChartSeriesFor(component: string, history: AdminInfrastructureHistory | null | undefined = this.history): UiMetricLineChartSeries[] {
    const serie = (history?.services || []).find((item) => item.component === component);
    if (!serie) {
      return [];
    }
    return [
      {
        key: `pods-total-${component}`,
        label: this.i18n.t('admin.overview.totalPodsLabel'),
        color: '#38bdf8',
        points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
          timestamp: point.capturedAt,
          bucketStart: point.bucketStart,
          bucketEnd: point.bucketEnd,
          value: point.totalPods
        })), history)
      },
      {
        key: `pods-ready-${component}`,
        label: this.i18n.t('admin.overview.readyPodsLabel'),
        color: '#22c55e',
        points: this.toBucketedHistoryPoints(serie.points.map((point) => ({
          timestamp: point.capturedAt,
          bucketStart: point.bucketStart,
          bucketEnd: point.bucketEnd,
          value: point.readyPods
        })), history)
      }
    ].filter((item) => item.points.some((point) => point.value != null));
  }

  private serviceHttpChartSeriesFor(component: string, history: AdminInfrastructureHistory | null | undefined = this.history): UiMetricLineChartSeries[] {
    const serie = (history?.services || []).find((item) => item.component === component);
    if (!serie) {
      return [];
    }

    return [
      {
        key: `http-in-${component}`,
        label: this.i18n.t('admin.overview.incomingLabel'),
        color: '#38bdf8',
        points: this.toCounterRatePoints(serie.points, 'incomingHttpRequestCount', history)
      },
      {
        key: `http-out-${component}`,
        label: this.i18n.t('admin.overview.outgoingLabel'),
        color: '#f59e0b',
        points: this.toCounterRatePoints(serie.points, 'outgoingHttpRequestCount', history)
      }
    ].filter((item) => item.points.some((point) => point.value != null));
  }

  private buildHttpCounterRateSeries(history: AdminInfrastructureHistory | null | undefined,
                                     field: HttpCounterField,
                                     labelSuffix: string,
                                     alpha?: number): UiMetricLineChartSeries[] {
    return (history?.services || [])
      .map((serie, index) => {
        const color = this.chartPalette[index % this.chartPalette.length];
        return {
          key: `${field}-${serie.component}`,
          label: `${serie.displayName} ${labelSuffix}`.trim(),
          color: alpha != null ? this.withAlpha(color, alpha) : color,
          points: this.toCounterRatePoints(serie.points, field, history)
        };
      })
      .filter((serie) => serie.points.some((point) => point.value != null));
  }

  private toCounterRatePoints(points: AdminInfrastructureServicePoint[],
                              field: HttpCounterField,
                              history: AdminInfrastructureHistory | null | undefined = this.history): UiMetricLineChartPoint[] {
    const intervalMs = this.historyAggregationIntervalFor(history);
    return points.map((point, index) => {
      const currentValue = point[field];
      if (index === 0 || currentValue == null) {
        return this.toChartPoint(point.bucketStart ?? point.capturedAt, null, intervalMs, point.bucketEnd ?? undefined);
      }

      const previousPoint = points[index - 1];
      const previousValue = previousPoint[field];
      if (previousValue == null) {
        return this.toChartPoint(point.bucketStart ?? point.capturedAt, null, intervalMs, point.bucketEnd ?? undefined);
      }

      const elapsedMs = Date.parse(point.capturedAt) - Date.parse(previousPoint.capturedAt);
      const delta = currentValue - previousValue;
      if (!Number.isFinite(elapsedMs) || elapsedMs <= 0 || delta < 0) {
        return this.toChartPoint(point.bucketStart ?? point.capturedAt, null, intervalMs, point.bucketEnd ?? undefined);
      }

      return this.toChartPoint(point.bucketStart ?? point.capturedAt, (delta / elapsedMs) * 60_000, intervalMs, point.bucketEnd ?? undefined);
    });
  }

  private toBucketedHistoryPoints(points: Array<{ timestamp: string; value: number | null; bucketStart?: string | null; bucketEnd?: string | null }>,
                                  history: AdminInfrastructureHistory | null | undefined): UiMetricLineChartPoint[] {
    const intervalMs = this.historyAggregationIntervalFor(history);
    return points.map((point) => this.toChartPoint(point.bucketStart ?? point.timestamp, point.value, intervalMs, point.bucketEnd ?? undefined));
  }

  private historyAggregationIntervalFor(history: AdminInfrastructureHistory | null | undefined): number {
    if (this.chartModal?.history === history && this.chartModal) {
      return this.chartModal.selectedAggregationIntervalMs;
    }
    return this.selectedHistoryAggregationIntervalMs;
  }

  private toChartPoint(timestamp: string, value: number | null, intervalMs: number, bucketEndOverride?: string): UiMetricLineChartPoint {
    const start = Date.parse(timestamp);
    const bucketStart = Number.isFinite(start) ? start : Date.now();
    const safeIntervalMs = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : 1_000;
    const parsedBucketEnd = bucketEndOverride ? Date.parse(bucketEndOverride) : Number.NaN;
    const bucketEnd = Number.isFinite(parsedBucketEnd) && parsedBucketEnd > bucketStart
      ? parsedBucketEnd
      : bucketStart + safeIntervalMs;
    return {
      bucketStart: new Date(bucketStart).toISOString(),
      bucketEnd: new Date(bucketEnd).toISOString(),
      value
    };
  }
}

type ServiceChartMetric = 'cpu' | 'memory' | 'pods' | 'http';
type GlobalChartKind = 'db-connections' | 'db-pressure' | 'db-timing' | 'db-latency' | 'http-inbound' | 'http-outbound' | 'http-errors' | 'http-latency' | 'command-executions';
type ChartModalKind = 'service-cpu' | 'service-memory' | 'service-pods' | 'service-http' | GlobalChartKind;
type HttpCounterField = 'incomingHttpRequestCount' | 'incomingHttpErrorCount' | 'outgoingHttpRequestCount' | 'outgoingHttpErrorCount';

interface ChartModalState {
  kind: ChartModalKind;
  component: string | null;
  title: string;
  subtitle: string;
  infoTooltip: string;
  valueSuffix: string;
  history: AdminInfrastructureHistory | null;
  loading: boolean;
  historyMode: 'relative' | 'absolute';
  selectedRangeMinutes: number;
  selectedAggregationIntervalMs: number;
  absoluteFromInput: string;
  absoluteToInput: string;
}

type OverviewMetricHelpKey =
  'helpPodAvailability' |
  'helpDatabase' |
  'helpServicesHealth' |
  'helpPods' |
  'helpRestarts' |
  'helpServiceHealth';

type ServicePointLike = Pick<AdminInfrastructureServicePoint,
  'capturedAt' | 'overallStatus' | 'totalPods' | 'readyPods' | 'restartCount' | 'cpuUsagePercent' | 'memoryUsageBytes' |
  'activeDbConnections' | 'idleDbConnections' | 'pendingDbConnections' | 'maxDbConnections' |
  'connectionAcquireMs' | 'connectionCreationMs' | 'connectionUsageMs' | 'connectionTimeoutCount' |
  'incomingHttpRequestCount' | 'incomingHttpErrorCount' | 'incomingHttpLatencyMs' |
  'outgoingHttpRequestCount' | 'outgoingHttpErrorCount' | 'outgoingHttpLatencyMs'>;

type PodPointLike = Pick<AdminInfrastructurePodPoint,
  'capturedAt' | 'phase' | 'healthStatus' | 'ready' | 'restartCount' | 'cpuMilliCores' | 'memoryBytes'>;
